import React from "react";
import { subscribeUserUiPreferences } from "../lib/userUiPreferencesSync.js";
import { useSearchParams } from "react-router";
import { fetchGeotabPreferences, fetchGeotabStatus, saveGeotabPreferences } from "../api/geotabApi.js";
import useAccess from "../hooks/useAccess.jsx";
import { rememberGeotabTolerance } from "../hooks/useGeotabTolerance.js";
import { ACCESS_PERMISSION_KEYS } from "../lib/accessControl.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import { getLocalTodayDispatchDate } from "../utils/dispatchDayCalendarUtils.js";
import { readLastDispatchContext } from "../utils/dispatchDateUrls.js";
import GeotabCalibrationView from "./geotab/GeotabCalibrationView.jsx";
import GeotabComparisonView from "./geotab/GeotabComparisonView.jsx";
import GeotabDashboardView from "./geotab/GeotabDashboardView.jsx";
import GeotabDevicesView from "./geotab/GeotabDevicesView.jsx";
import GeotabLiveView from "./geotab/GeotabLiveView.jsx";
import { normalizePreferences, readGeotabSession, writeGeotabSession } from "./geotab/geotabPreferences.js";
import "./geotab/GeotabDashboard.css";

// Comme au Data Manager : un clic dans un tableau surligne sa ligne pour suivre ses valeurs.
// Une ligne d'une seule cellule (détail déplié, titre de groupe) n'est pas une ligne de données.
// Écouté en capture : certains boutons de ligne arrêtent la propagation du clic.
function selectTableRow(event) {
  const row = event.target.closest?.("tbody > tr");
  if (!row || row.cells.length < 2) return;
  row.closest("table").querySelectorAll(":scope > tbody > tr[data-geotab-row-selected]").forEach((tr) => tr.removeAttribute("data-geotab-row-selected"));
  row.setAttribute("data-geotab-row-selected", "");
}

const VIEWS = [
  // Même droit que le Calibrage : barre et chiffres par chauffeur, sur les journées clôturées.
  ["dashboard", "Tableau de bord", ACCESS_PERMISSION_KEYS.geotabCalibrationRead],
  ["comparison", "Dispatch vs Geotab", ACCESS_PERMISSION_KEYS.geotabRead],
  ["live", "Carte en direct", ACCESS_PERMISSION_KEYS.geotabLiveRead],
  ["calibration", "Calibrage", ACCESS_PERMISSION_KEYS.geotabCalibrationRead],
  ["devices", "Camions et boîtiers", ACCESS_PERMISSION_KEYS.geotabDevicesRead],
];

export default function GeotabPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAccess();
  // Sans vue ni date dans l'adresse : celles choisies dans Geotab depuis la connexion, sinon celles du Dispatch.
  const dispatchContext = React.useMemo(() => readLastDispatchContext(), []);
  const [session] = React.useState(readGeotabSession);
  const availableViews = VIEWS.filter(([, , permission]) => hasPermission(permission));
  const requestedView = searchParams.get("view") || session.view || availableViews[0]?.[0];
  const view = availableViews.some(([key]) => key === requestedView) ? requestedView : availableViews[0]?.[0];
  const hasView = Boolean(view);
  const date = searchParams.get("date") || session.date || dispatchContext.dispatch_date || getLocalTodayDispatchDate();
  const periodParam = searchParams.get("period") || session.period || dispatchContext.dispatch_period;
  const period = periodParam === "evening" ? "evening" : "day";
  const truck = searchParams.get("truck") || null;
  const [status, setStatus] = React.useState(null);
  const [preferences, setPreferences] = React.useState(null);
  const latestPreferences = React.useRef(null);
  const savingPreferences = React.useRef(Promise.resolve());
  const pendingPreferences = React.useRef(0);
  React.useEffect(() => subscribeUserUiPreferences("geotab", (saved) => {
    if (!latestPreferences.current || pendingPreferences.current) return false;
    const next = normalizePreferences(saved);
    if (JSON.stringify(next) !== JSON.stringify(latestPreferences.current)) {
      latestPreferences.current = next;
      setPreferences(next);
    }
    return true;
  }), []);

  React.useEffect(() => {
    if (!hasView) {
      setStatus(null);
      setPreferences(null);
      return;
    }
    let cancelled = false;
    fetchGeotabStatus().then((saved) => { if (!cancelled) setStatus(saved); })
      .catch(() => { if (!cancelled) setStatus({ configured: false }); });
    fetchGeotabPreferences().catch(() => null).then((saved) => {
      if (cancelled) return;
      latestPreferences.current = normalizePreferences(saved);
      setPreferences(latestPreferences.current);
    });
    return () => { cancelled = true; };
  }, [hasView]);
  React.useEffect(() => {
    if (!view || requestedView === view) return;
    const next = new URLSearchParams(searchParams);
    next.set("view", view);
    setSearchParams(next, { replace: true });
  }, [view, requestedView, searchParams, setSearchParams]);
  const chosen = Boolean(searchParams.get("date") || searchParams.get("period"));
  React.useEffect(() => {
    if (chosen && hasView) writeGeotabSession({ date, period });
  }, [chosen, date, period, hasView]);
  React.useEffect(() => { if (view) writeGeotabSession({ view }); }, [view]);
  const thresholds = React.useMemo(() => preferences && {
    radius_m: preferences.radius_m, tolerance_min: preferences.tolerance_min, min_off_min: preferences.min_off_min,
  }, [preferences?.radius_m, preferences?.tolerance_min, preferences?.min_off_min]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
    setSearchParams(next, { replace: true });
  };
  // Enregistrés l'un après l'autre pour que le dernier choix l'emporte.
  const changePreferences = (patch) => {
    pendingPreferences.current++;
    const next = { ...latestPreferences.current, ...patch };
    latestPreferences.current = next;
    setPreferences(next);
    rememberGeotabTolerance(patch.tolerance_min);
    savingPreferences.current = savingPreferences.current.then(() => saveGeotabPreferences(next)).catch(() => {})
      .finally(() => { pendingPreferences.current--; });
  };

  if (!hasView) return (
    <main className="mx-auto w-full px-3 py-2" data-geotab-page="true">
      <p role="alert" className="rounded-[4px] border border-slate-200 bg-white p-3 text-sm text-slate-700">
        Vous n’avez accès à aucun onglet Geotab.
      </p>
    </main>
  );

  return (
    // Prévu vs Réel occupe toute la hauteur de l'écran : la carte s'étire jusqu'en bas.
    <main className={`mx-auto grid w-full min-w-0 grid-cols-[minmax(0,1fr)] gap-2 px-3 pt-0 ${view === "comparison" ? "pb-2 min-[1281px]:flex-1 min-[1281px]:grid-rows-[auto_1fr]" : "pb-6"} ${resolvePageSurfaceWidthClassName("geotab")}`} data-geotab-page="true" onClickCapture={selectTableRow}>
      <nav className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-1" role="tablist" aria-label="Vues Geotab">
        {availableViews.map(([key, label]) => (
          <button key={key} type="button" aria-selected={view === key} role="tab" onClick={() => update({ view: key })}
            className={`rounded-[4px] border px-2 py-1 text-[13.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${view === key ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"}`}>
            {label}
          </button>
        ))}
        {status?.configured && <span className="ml-auto text-[11.5px] text-slate-500">Geotab connecté · {status.database} · lecture seule</span>}
      </nav>
      {status && !status.configured ? (
        <div role="alert" className="rounded-[4px] border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-[inset_3px_0_0_#a86a24]">
          <b>Connexion Geotab non configurée.</b> Ajouter <code>GEOTAB_DATABASE</code>, <code>GEOTAB_USERNAME</code> et <code>GEOTAB_PASSWORD</code>
          {" "}(utilisateur MyGeotab en lecture seule) dans la configuration du serveur.
        </div>
      ) : !status || !preferences ? (
        <p role="status" className="text-sm text-slate-500">Chargement…</p>
      ) : view === "dashboard" ? (
        <GeotabDashboardView period={period} thresholds={thresholds} onPeriodChange={(value) => update({ period: value })}
          preferences={preferences} onPreferencesChange={changePreferences} onOpenView={(next, patch = {}) => update({ view: next, ...patch })} />
      ) : view === "live" ? (
        <GeotabLiveView initialTruck={truck} tolerance={thresholds.tolerance_min} onTruckChange={(id) => update({ truck: id })}
          preferences={preferences} onPreferencesChange={changePreferences} />
      ) : view === "calibration" ? (
        <GeotabCalibrationView period={period} thresholds={thresholds} onPeriodChange={(value) => update({ period: value })}
          preferences={preferences} onPreferencesChange={changePreferences} canApply={hasPermission(ACCESS_PERMISSION_KEYS.geotabCalibrationManage)} />
      ) : view === "devices" ? (
        <GeotabDevicesView canManage={hasPermission(ACCESS_PERMISSION_KEYS.geotabManage)} />
      ) : (
        <GeotabComparisonView
          date={date} period={period} thresholds={thresholds} initialTruck={truck}
          onDateChange={(value) => update({ date: value })} onPeriodChange={(value) => update({ period: value })}
          preferences={preferences} onPreferencesChange={changePreferences} onTruckChange={(id) => update({ truck: id })}
        />
      )}
    </main>
  );
}
